// 响应式主框架：
// - < md（手机 / Capacitor 包壳）：底部 5 tab TabBar + 沉浸式路由全屏
// - ≥ md（桌面）：左侧 Sider 菜单（mirror 手机 5 tab 结构）+ 顶部品牌 header；
//   content 用 mobile 宽度（max 480px 居中）渲染同一套 m-tabs 组件，避免双套维护
import { Button, Layout, Menu, Breadcrumb } from 'antd';
import type { MenuProps } from 'antd';
import { LeftOutlined, VideoCameraOutlined, FileTextOutlined, ThunderboltOutlined, MessageOutlined, UserOutlined, ShopOutlined, HomeOutlined, AppstoreOutlined } from '@ant-design/icons';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo, type ReactNode } from 'react';
import { MODULES } from '../modules/_manifest.js';
import LocaleSwitcher from './LocaleSwitcher.js';
import StoreSelector from './StoreSelector.js';
import BottomTabBar, { BOTTOM_TAB_BAR_HEIGHT } from './BottomTabBar.js';
import { useBreakpoint } from '../hooks/useBreakpoint.js';
import { useRegion } from '../hooks/useRegion.js';
import { capabilityForModule, moduleState, moduleVisibleInNavigation, type CapabilityArea } from '../capabilities.js';
import { routeHidesBottomTab } from '../mobile-route-meta.js';
import { mobileType } from '../theme/mobile-typography.js';

const BRAND_PRIMARY = '#FF7043';

/** 桌面模块在手机视口的简易顶栏（返回 + 标题） */
function MobileModuleHeader({ title }: { title: string }) {
	const navigate = useNavigate();
	return (
		<div style={{
			display: 'flex',
			alignItems: 'center',
			height: 48,
			padding: '0 8px',
			background: '#fff',
			borderBottom: '1px solid #f0f0f0',
			position: 'sticky',
			top: 0,
			zIndex: 10,
		}}>
			<Button
				type="text"
				icon={<LeftOutlined style={{ color: BRAND_PRIMARY, fontSize: 18 }} />}
				onClick={() => navigate(-1)}
			/>
			<span style={{ flex: 1, textAlign: 'center', ...mobileType.topBar, marginInlineEnd: 32 }}>
				{title}
			</span>
		</div>
	);
}

/** 自己渲染 MobileHeader 的路由前缀 — layout 跳过 MobileModuleHeader 避免双标题 */
const ROUTES_WITH_OWN_HEADER = ['/reportaudit', '/aipatrol', '/inspect'];

export default function MobileFirstLayout({ children }: { children: ReactNode }) {
	const { t } = useTranslation();
	const location = useLocation();
	const { isMobile } = useBreakpoint();
	const region = useRegion();

	// === 所有 hook 必须在条件 return 之前调用（Rules of Hooks）===
	// 桌面 sider 菜单：mirror 手机 5 tab + 工作台下 27 模块 + 我的下子页。手机模式不用，但仍要计算（hook 顺序固定）
	const menuItems: MenuProps['items'] = useMemo(() => {
		// APAC / Mexico: one capability registry filters CN-only or permission-gated modules.
		const usableModules = MODULES.filter((m) =>
			m.key !== 'auth' && m.key !== 'dashboard' && moduleVisibleInNavigation(m, region)
		);
		const moduleItemsFor = (area: CapabilityArea): NonNullable<MenuProps['items']> =>
			usableModules
				.filter((m) => capabilityForModule(m.key)?.area === area)
				.map((m) => ({
					key: m.path,
					label: <NavLink to={m.path}>{t(`modules.${m.key}`)}</NavLink>,
				}));
		if (region === 'apac') {
			return [
				{
					key: '/m/home',
					icon: <HomeOutlined />,
					label: <NavLink to="/m/home">{t('tabbar.home')}</NavLink>,
				},
				{
					key: '/m/monitor',
					icon: <ShopOutlined />,
					label: <NavLink to="/m/monitor">{t('tabbar.store')}</NavLink>,
					children: moduleItemsFor('monitor'),
				},
				{
					key: '/m/application',
					icon: <AppstoreOutlined />,
					label: <NavLink to="/m/application">{t('tabbar.application')}</NavLink>,
					children: [
						...(moduleItemsFor('incidents') ?? []),
						...(moduleItemsFor('analytics') ?? []),
						...(moduleItemsFor('operations') ?? []),
					],
				},
				{
					key: '/m/account',
					icon: <UserOutlined />,
					label: <NavLink to="/m/account">{t('tabbar.profile')}</NavLink>,
					children: [
						{ key: '/m/profile-edit', label: <NavLink to="/m/profile-edit">{t('mtab.profile.edit_profile')}</NavLink> },
						{ key: '/m/recent',       label: <NavLink to="/m/recent">{t('mtab.profile.recent')}</NavLink> },
						{ key: '/m/album',        label: <NavLink to="/m/album">{t('mtab.profile.album')}</NavLink> },
						{ key: '/m/repair-list',  label: <NavLink to="/m/repair-list">{t('mtab.profile.repair_log')}</NavLink> },
						{ key: '/m/hotline',      label: <NavLink to="/m/hotline">{t('mtab.profile.hotline')}</NavLink> },
						{ key: '/m/feedback',     label: <NavLink to="/m/feedback">{t('mtab.profile.feedback')}</NavLink> },
						{ key: '/m/changelog',    label: <NavLink to="/m/changelog">{t('mtab.profile.changelog')}</NavLink> },
						{ key: '/m/settings',     label: <NavLink to="/m/settings">{t('mtab.profile.settings')}</NavLink> },
					],
				},
			];
		}
		return [
			{
				key: '/m/monitor',
				icon: <VideoCameraOutlined />,
				label: <NavLink to="/m/monitor">{t('tabbar.monitor')}</NavLink>,
				children: moduleItemsFor('monitor'),
			},
			{
				key: '/m/incidents',
				icon: <MessageOutlined />,
				label: <NavLink to="/m/incidents">{t('tabbar.incidents')}</NavLink>,
				children: moduleItemsFor('incidents'),
			},
			{
				key: '/m/analytics',
				icon: <FileTextOutlined />,
				label: <NavLink to="/m/analytics">{t('tabbar.analytics')}</NavLink>,
				children: moduleItemsFor('analytics'),
			},
			{
				key: '/m/operations',
				icon: <ThunderboltOutlined />,
				label: <NavLink to="/m/operations">{t('tabbar.operations')}</NavLink>,
				children: moduleItemsFor('operations'),
			},
			{
				key: '/m/account',
				icon: <UserOutlined />,
				label: <NavLink to="/m/account">{t('tabbar.account')}</NavLink>,
				children: [
					{ key: '/m/profile-edit', label: <NavLink to="/m/profile-edit">{t('mtab.profile.edit_profile')}</NavLink> },
					{ key: '/m/recent',       label: <NavLink to="/m/recent">{t('mtab.profile.recent')}</NavLink> },
					{ key: '/m/album',        label: <NavLink to="/m/album">{t('mtab.profile.album')}</NavLink> },
					{ key: '/m/repair-list',  label: <NavLink to="/m/repair-list">{t('mtab.profile.repair_log')}</NavLink> },
					{ key: '/m/hotline',      label: <NavLink to="/m/hotline">{t('mtab.profile.hotline')}</NavLink> },
					{ key: '/m/feedback',     label: <NavLink to="/m/feedback">{t('mtab.profile.feedback')}</NavLink> },
					{ key: '/m/changelog',    label: <NavLink to="/m/changelog">{t('mtab.profile.changelog')}</NavLink> },
					{ key: '/m/settings',     label: <NavLink to="/m/settings">{t('mtab.profile.settings')}</NavLink> },
				],
			},
		];
	}, [t, region]);

	const currentModule = MODULES
		.filter((m) => m.key !== 'auth')
		.find((m) => location.pathname.startsWith(m.path));
	const currentModuleState = currentModule ? moduleState(currentModule, region) : 'available';

	const isApacFavoriteStorePage =
		region === 'apac'
		&& location.pathname === '/m/video'
		&& new URLSearchParams(location.search).get('favorite') === '1';
	const hideBottomTab = routeHidesBottomTab(location.pathname)
		|| isApacFavoriteStorePage
		|| (region === 'apac' && (
			/^\/m\/home\/.+/.test(location.pathname)
			|| /^\/m\/(profile-edit|recent|album|repair|repair-list|settings)(?:\/|$)/.test(location.pathname)
			|| /^\/(event|evaluation|inspect|customer|traffic|heatmap|leavepost|scan|staff|rewardpunish|smart-listening|phonelive|wechatvip|university|datawarn)(?:\/|$)/.test(location.pathname)
		));
	const isMTabRoute = location.pathname.startsWith('/m/');
	const ownsHeader = ROUTES_WITH_OWN_HEADER.some((p) => location.pathname.startsWith(p));
	const showModuleHeader = !hideBottomTab
		&& !isMTabRoute
		&& !ownsHeader
		&& !!currentModule
		&& currentModule.key !== 'dashboard'
		&& !(region === 'apac' && currentModuleState !== 'available');

	// =========== 手机模式 ===========
	if (isMobile) {
		const shellClassName = region === 'apac'
			? 'ulucu-mobile-shell ulucu-mobile-shell--apac'
			: 'ulucu-mobile-shell';
		return (
			<Layout
				className={shellClassName}
				data-region={region}
				style={{ minHeight: '100vh', background: region === 'apac' ? '#f5f6f8' : '#f5f5f5' }}
			>
				{showModuleHeader && (
					<MobileModuleHeader title={t(`modules.${currentModule!.key}`)} />
				)}
				<Layout.Content
					style={{
						paddingBottom: hideBottomTab
							? 0
							: `calc(${BOTTOM_TAB_BAR_HEIGHT}px + 8px + env(safe-area-inset-bottom))`,
						minHeight: '100vh',
					}}
				>
					{children}
				</Layout.Content>
				{!hideBottomTab && <BottomTabBar />}
			</Layout>
		);
	}

	// =========== 桌面模式 ===========
	// selectedKey: 优先匹配 /m/* 完整路径，否则匹配 module.path（命中工作台子项）
	const selectedKey = (() => {
		const p = location.pathname;
		if (p.startsWith('/m/')) {
			// 精准匹配；找不到时退到 /m/<root>
			const exact = ['/m/profile-edit','/m/recent','/m/album','/m/repair-list','/m/hotline','/m/feedback','/m/changelog','/m/settings'];
			const hit = exact.find((k) => p.startsWith(k));
			if (hit) return hit;
			if (p.startsWith('/m/home')) return '/m/home';
			if (p.startsWith('/m/monitor') || p.startsWith('/m/video') || p.startsWith('/m/player') || p.startsWith('/m/multi-view') || p.startsWith('/m/multi-select')) return '/m/monitor';
			if (region === 'apac' && (p.startsWith('/m/application') || p.startsWith('/m/incidents') || p.startsWith('/m/messages') || p.startsWith('/m/analytics') || p.startsWith('/m/reports') || p.startsWith('/m/operations') || p.startsWith('/m/workbench'))) return '/m/application';
			if (p.startsWith('/m/incidents') || p.startsWith('/m/messages')) return '/m/incidents';
			if (p.startsWith('/m/analytics') || p.startsWith('/m/reports')) return '/m/analytics';
			if (p.startsWith('/m/operations') || p.startsWith('/m/workbench')) return '/m/operations';
			if (p.startsWith('/m/account') || p.startsWith('/m/profile')) return '/m/account';
		}
		return currentModule?.path ?? '/m/monitor';
	})();

	// 面包屑当前页标题：/m/* 走 tabbar/profile 词条；/<module> 走 modules.<key>
	const breadcrumbTitle = (() => {
		const labels: Record<string, string> = {
			'/m/home': t('tabbar.home'),
			'/m/monitor': region === 'apac' ? t('tabbar.store') : t('tabbar.monitor'),
			'/m/application': t('tabbar.application'),
			'/m/incidents': t('tabbar.incidents'),
			'/m/analytics': t('tabbar.analytics'),
			'/m/operations': t('tabbar.operations'),
			'/m/account': region === 'apac' ? t('tabbar.profile') : t('tabbar.account'),
			'/m/profile-edit': t('mtab.profile.edit_profile'),
			'/m/recent': t('mtab.profile.recent'),
			'/m/album': t('mtab.profile.album'),
			'/m/repair-list': t('mtab.profile.repair_log'),
			'/m/hotline': t('mtab.profile.hotline'),
			'/m/feedback': t('mtab.profile.feedback'),
			'/m/changelog': t('mtab.profile.changelog'),
			'/m/settings': t('mtab.profile.settings'),
		};
		if (labels[selectedKey]) return labels[selectedKey];
		if (currentModule && currentModule.key !== 'dashboard') return t(`modules.${currentModule.key}`);
		return '';
	})();

	return (
		<Layout style={{ minHeight: '100vh' }}>
			<Layout.Header
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 16,
					paddingInline: 24,
					background: BRAND_PRIMARY,
					height: 56,
				}}
			>
				<Link to="/" style={{ color: 'white', fontWeight: 600, fontSize: 18, letterSpacing: 2, marginInlineEnd: 24 }}>
					{t('app.brand_zh')}
				</Link>
				<Breadcrumb
					style={{ flex: 1 }}
					items={breadcrumbTitle ? [{ title: <span style={{ color: 'white' }}>{breadcrumbTitle}</span> }] : []}
				/>
				<StoreSelector syncGlobal />
				<LocaleSwitcher />
			</Layout.Header>

			<Layout>
				<Layout.Sider width={240} theme="light" style={{ overflowY: 'auto' }}>
					<Menu
						mode="inline"
						selectedKeys={[selectedKey]}
						defaultOpenKeys={['/m/monitor', '/m/incidents', '/m/analytics', '/m/operations', '/m/account']}
						style={{ borderInlineEnd: 'none', height: '100%' }}
						items={menuItems}
					/>
				</Layout.Sider>

				<Layout.Content style={{ background: '#f0f2f5', overflowY: 'auto', minHeight: 'calc(100vh - 56px)' }}>
					{children}
				</Layout.Content>
			</Layout>
		</Layout>
	);
}
